import "./pollyfills";

import { Auth0Provider } from "@auth0/auth0-react";
import { Toaster, TooltipProvider } from "@cca/components";
import { SettingsProvider } from "@contexts";
import config from "@core/config";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { extend } from "dayjs";
import duration from "dayjs/plugin/duration";
import * as React from "react";
import { CookiesProvider } from "react-cookie";
import { createRoot } from "react-dom/client";

import RouterProvider from "./router";

import "react-complex-tree/lib/style-modern.css";
import "./index.css";

extend(duration);

async function enableMocking(): Promise<ServiceWorkerRegistration | undefined> {
  const { setupWorker } = await import("msw/browser");
  const { httpHandlers } = await import("@tests/mocks/server/handlers");
  const worker = setupWorker(...httpHandlers);

  return worker.start();
}

function printInfo() {
  // eslint-disable-next-line no-console
  console.log(
    `===== CCA UNIVERISTY =====\nEnvironment: ${config.NODE_ENV}\nTag_name: ${config.TAG_NAME} \n===== Enjoy our application =====`,
  );
}

function renderApp(): void {
  printInfo();
  const rootElement = document.getElementById("root");
  if (!rootElement) return console.error("Root element not found");

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 10,
      },
    },
  });

  createRoot(rootElement).render(
    <React.StrictMode>
      <CookiesProvider defaultSetOptions={{ path: "/" }}>
        <Auth0Provider
          authorizationParams={{
            redirect_uri: window.location.origin,
            connection: config.AUTH0_CONNECTION,
            audience: `https://${config.AUTH0_DOMAIN}/api/v2/`,
            scope: config.AUTH0_SCOPE,
          }}
          cacheLocation="localstorage"
          clientId={config.AUTH0_CLIENT_ID}
          domain={config.AUTH0_DOMAIN}
          useRefreshTokens
        >
          <QueryClientProvider client={queryClient}>
            <SettingsProvider>
              <Toaster />
              <TooltipProvider>
                <RouterProvider />
              </TooltipProvider>
            </SettingsProvider>
          </QueryClientProvider>
        </Auth0Provider>
      </CookiesProvider>
    </React.StrictMode>,
  );
}

if (process.env.MOCKED === "true") {
  enableMocking().then(renderApp);
} else {
  renderApp();
}
